/**
 * Which sign-up screen a /dashboard path shows (screenshots/93–97), and the two steps that happen between screens:
 * verifying the emailed link (/dashboard/signin?token=) and accepting an invite once signed in.
 *   signed out  /dashboard/signup[?as=office] · /dashboard/invite/<id> · /dashboard/invite (sign in, then accept)
 *               anything else: sign in, then back to that page
 *   signed in   /dashboard/invite[/<id>][/accept]: accept, then onboarding (/dashboard/welcome)
 */
import { useEffect, useState } from "react";
import { authClient } from "./authClient";
import { acceptInviteTo, betterAuthApi, loadInvite, verifyLink } from "./authApi";
import { AuthLayout } from "./AuthLayout";
import { AuthPage } from "./AuthPage";
import { LanyardBadge } from "./LanyardBadge";
import { passView } from "./civicPass";
import { navigate } from "../app/router";
import type { Invite } from "./signupFlow";

const sitePath = (p: string | null) => (p && /^\/(?![/\\])/.test(p) && p.length <= 2000 ? p : null);
const EMPTY = passView({ name: "", email: "", emailVerified: false, group: null, topics: [], follows: [] });

/** Between screens: the bare layout with an empty pass (nothing to say yet). */
const Waiting = ({ error }: { error?: string | null }) => (
  <AuthLayout stage={<LanyardBadge view={EMPTY} />}><div className="auth__body">{error && <p role="alert" className="auth__error">{error}</p>}</div></AuthLayout>
);

export function isAuthPath(path: string) { return /^\/dashboard\/(signin|signup|invite)(\/|$)/.test(path); }

export function SignedOut({ path }: { path: string }) {
  const q = new URLSearchParams(location.search);
  const token = q.get("token");
  const inviteId = path.match(/^\/dashboard\/invite\/([^/]+)/)?.[1];
  const [invite, setInvite] = useState<Invite | null | undefined>(inviteId ? undefined : null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      void verifyLink(token).then((r) => {
        if (r.ok) location.replace(sitePath(q.get("next")) ?? "/dashboard");
        else setError(r.message ?? null);
      });
    }
    if (inviteId) void loadInvite(decodeURIComponent(inviteId)).then(setInvite);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (token && !error) return <Waiting />;
  if (invite === undefined) return <Waiting />;
  if (invite) return <AuthPage mode="invite" invite={invite} api={betterAuthApi} />;
  // Sign in and Get a pass are one AuthPage: switching changes the path in place, so the layout and the pass stay put.
  const signup = /^\/dashboard\/signup\/?$/.test(path);
  const back = signup ? undefined : /^\/dashboard\/(signin|invite)/.test(path) ? (path.startsWith("/dashboard/invite") ? "/dashboard/invite/accept" : sitePath(q.get("next")) ?? "/dashboard") : path + location.search;
  return <AuthPage mode={signup ? "signup" : "signin"} intendedGroup={signup && q.get("as") === "office" ? "office" : null} api={betterAuthApi} callbackURL={back}
    initialError={error} onSwitchMode={(m) => navigate(m === "signin" ? "/dashboard/signin" : "/dashboard/signup")} />;
}

/** Signed in on an invite path: join the team (by id, or the first pending invitation for your email). */
export function AcceptInvite({ path }: { path: string }) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const id = path.match(/^\/dashboard\/invite\/([^/]+)/)?.[1];
    void (async () => {
      let invitationId = id && id !== "accept" ? decodeURIComponent(id) : null;
      if (!invitationId) {
        const r = await authClient.organization.listUserInvitations();
        invitationId = (r.data ?? []).find((i) => i.status === "pending")?.id ?? null;
      }
      const joined = invitationId ? await acceptInviteTo(invitationId) : "";
      if (joined === null) { setError("This invite is no longer valid."); return; }
      // The Chicago Politics team (platform organization "dashboard") lands on Approvals; everyone else on onboarding.
      location.replace(joined === "dashboard" ? "/dashboard/admin" : "/dashboard/welcome");
    })();
  }, [path]);
  return <Waiting error={error} />;
}
